// src/auth/signupProvisioning.js
//
// Shared signup provisioning helpers.
//
// Rules for the self-serve path:
//   - Non-invited users should end up with a pending default org,
//     an orgAdmin membership, and a default study.
//   - Invited users should NOT get a personal org; they continue
//     through the invite-accept flow after authentication.
//   - Provisioning must be resumable so a partial prior signup can
//     attach the missing membership / study without creating duplicates.

import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { ensureMembership } from './membershipHelpers';
import { createOrganization } from '../data/organizationsHelpers';
import { createStudy, loadStudiesForOrg } from '../data/studiesHelpers';
import { listPendingInvitesForEmail } from '../data/invitesHelpers';

export const DEFAULT_STUDY_NAME = 'Default Study';
export const DEFAULT_STUDY_DESCRIPTION =
  'Automatically created when the organization was signed up.';

function toTitleCase(value) {
  return String(value || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase())
    .join(' ');
}

function toPossessive(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return /s$/i.test(trimmed) ? `${trimmed}'` : `${trimmed}'s`;
}

export function deriveDefaultOrganizationName({ displayName = '', email = '' } = {}) {
  const trimmedName = String(displayName || '').trim();
  if (trimmedName) {
    return `${toPossessive(trimmedName)} Organization`;
  }

  const normalizedEmail = String(email || '').toLowerCase().trim();
  const emailLocalPart = normalizedEmail.split('@')[0] || '';
  const fallbackOwner = toTitleCase(emailLocalPart.replace(/[._-]+/g, ' '));
  if (fallbackOwner) {
    return `${toPossessive(fallbackOwner)} Organization`;
  }

  return 'My Organization';
}

function snapshotToArray(snap) {
  const out = [];
  snap.forEach((docSnap) => out.push({ id: docSnap.id, ...docSnap.data() }));
  return out;
}

async function listMembershipsForUser(uid) {
  const snap = await getDocs(
    query(collection(db, 'memberships'), where('uid', '==', uid)),
  );
  return snapshotToArray(snap);
}

async function listOrganizationsCreatedBy(uid) {
  const snap = await getDocs(
    query(collection(db, 'organizations'), where('createdBy', '==', uid)),
  );
  const orgs = snapshotToArray(snap);
  orgs.sort((a, b) => {
    const statusScore = (status) => {
      if (status === 'pending') return 0;
      if (status === 'approved') return 1;
      if (status === 'suspended') return 2;
      if (status === 'rejected') return 3;
      return 4;
    };
    const statusDelta = statusScore(a.status) - statusScore(b.status);
    if (statusDelta !== 0) return statusDelta;
    return (a.name || '').localeCompare(b.name || '');
  });
  return orgs;
}

async function ensureDefaultStudyForOrganization(orgId, uid) {
  const studies = await loadStudiesForOrg(orgId);
  if (studies.length > 0) {
    return { createdStudy: false };
  }

  await createStudy({
    orgId,
    name: DEFAULT_STUDY_NAME,
    description: DEFAULT_STUDY_DESCRIPTION,
    createdBy: uid,
  });
  return { createdStudy: true };
}

export async function ensureSignupAccess({
  uid,
  email,
  displayName = '',
  requestedOrgName = '',
}) {
  if (!uid) throw new Error('ensureSignupAccess requires uid');
  const normalizedEmail = String(email || '').toLowerCase().trim();
  if (!normalizedEmail) throw new Error('ensureSignupAccess requires email');

  const memberships = await listMembershipsForUser(uid);
  if (memberships.length > 0) {
    return {
      kind: 'existing-membership',
      orgId: memberships[0].orgId || null,
      createdOrganization: false,
      createdStudy: false,
    };
  }

  const pendingInvites = await listPendingInvitesForEmail(normalizedEmail);
  if (pendingInvites.length > 0) {
    return {
      kind: 'pending-invite',
      inviteCount: pendingInvites.length,
      createdOrganization: false,
      createdStudy: false,
    };
  }

  const existingOrganizations = await listOrganizationsCreatedBy(uid);
  let orgId = existingOrganizations[0]?.id || null;
  let createdOrganization = false;

  if (!orgId) {
    const resolvedOrgName =
      String(requestedOrgName || '').trim()
      || deriveDefaultOrganizationName({
        displayName,
        email: normalizedEmail,
      });
    orgId = await createOrganization({
      name: resolvedOrgName,
      createdBy: uid,
    });
    createdOrganization = true;
  }

  await ensureMembership({
    uid,
    orgId,
    role: 'orgAdmin',
    email: normalizedEmail,
    displayName: String(displayName || '').trim(),
    createdBy: uid,
  });

  const { createdStudy } = await ensureDefaultStudyForOrganization(orgId, uid);

  return {
    kind: 'org-admin',
    orgId,
    createdOrganization,
    createdStudy,
  };
}
